const PIEVE_AUTO_MODEL_ID = 'AUTO';
const PIEVE_AUTO_FAST_MODEL_ID = 'AUTO_FAST';

// Sentinel models are selectable model values, not provider models. Every surface
// that needs a real provider model must convert them first via resolvePieveModelId()
// (shared/pieve/modelSelection.js). Each sentinel's reasoning levels must stay a
// subset of its target model's levels (enforced in modelCatalog.test.js).
const PIEVE_AUTO_MODEL = Object.freeze({
  id: PIEVE_AUTO_MODEL_ID,
  targetModelId: 'claude-sonnet-5-5',
  label: 'Auto',
  ui: {
    showInBasicComposer: true,
    showInAdvancedComposer: true,
    isSimpleModel: false,
    pickerLabel: 'Auto',
    pickerDescription: 'Automatically selects the best model for your task'
  },
  support: {
    reasoning: {
      enabled: true,
      levels: ['medium', 'high']
    }
  }
});

// Fast tier for lightweight read-only surfaces (analytics read threads). Hidden from
// pickers; assigned programmatically as a thread's composer model at creation. The
// failover descriptor is a latency/capacity fallback, not a provider-outage failover
// (that invariant is scoped to AUTO — see the auto-provider-failover initiative).
const PIEVE_AUTO_FAST_MODEL = Object.freeze({
  id: PIEVE_AUTO_FAST_MODEL_ID,
  targetModelId: 'gpt-6-luna',
  failover: Object.freeze({
    provider: 'openai',
    targetModelId: 'gpt-5.4-nano'
  }),
  label: 'Auto (Fast)',
  ui: {
    showInBasicComposer: false,
    showInAdvancedComposer: false,
    isSimpleModel: true,
    pickerLabel: 'Auto Fast',
    pickerDescription: 'Fast automatic model for quick data questions'
  },
  support: {
    reasoning: {
      enabled: true,
      levels: ['low']
    }
  }
});

const PIEVE_SENTINEL_MODELS = Object.freeze({
  [PIEVE_AUTO_MODEL_ID]: PIEVE_AUTO_MODEL,
  [PIEVE_AUTO_FAST_MODEL_ID]: PIEVE_AUTO_FAST_MODEL
});

const PIEVE_MODEL_MAP = Object.freeze({
  'gpt-6-luna': {
    id: 'gpt-6-luna',
    label: 'GPT-6 (Luna)',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 1_050_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.95, reasoning: 0.75, cost: 0.1 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: true,
      isSimpleModel: true,
      pickerLabel: 'GPT-6 Luna',
      pickerDescription: 'Fast model for focused questions and everyday tasks'
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh', 'max']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh', 'max']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: { enabled: true },
        supportsExplicitPromptCacheBoundary: true
      }
    }
  },
  'gpt-5.6-sol': {
    id: 'gpt-5.6-sol',
    label: 'GPT-5.6 (Sol)',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 1_050_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.2, reasoning: 1.0, cost: 0.92 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Codex 5.6 Sol',
      pickerDescription: "OpenAI's highest-tier GPT-5.6 Codex model"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh', 'max']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh', 'max']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        supportsExplicitPromptCacheBoundary: true
      }
    }
  },
  'gpt-5.6-terra': {
    id: 'gpt-5.6-terra',
    label: 'GPT-5.6 (Terra)',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 1_050_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.25, reasoning: 0.95, cost: 0.85 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: true,
      isSimpleModel: false,
      pickerLabel: 'Codex 5.6 Terra',
      pickerDescription: "OpenAI's newest Codex model with explicit prompt caching"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh', 'max']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh', 'max']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        supportsExplicitPromptCacheBoundary: true
      }
    }
  },
  'gpt-5.6-luna': {
    id: 'gpt-5.6-luna',
    label: 'GPT-5.6 (Luna)',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 1_050_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.35, reasoning: 0.85, cost: 0.7 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Codex 5.6 Luna',
      pickerDescription: "OpenAI's most economical GPT-5.6 Codex tier"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh', 'max']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh', 'max']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        supportsExplicitPromptCacheBoundary: true
      }
    }
  },
  'gpt-5.4': {
    id: 'gpt-5.4',
    label: 'GPT-5.4',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 1_050_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.3, reasoning: 0.9, cost: 0.85 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Codex 5.4',
      pickerDescription: "OpenAI's flagship model is optimized for complex tasks"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        // Explicit prompt-cache breakpoints require GPT-5.6 or newer on the OpenAI
        // Responses API. This older model keeps the capability false and falls back
        // to automatic caching via prompt_cache_key.
        supportsExplicitPromptCacheBoundary: false
      }
    }
  },
  'gpt-5.2-codex': {
    id: 'gpt-5.2-codex',
    label: 'GPT-5.2-Codex',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 258_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.5, reasoning: 0.7, cost: 0.5 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Codex 5.2',
      pickerDescription: 'Balanced for everyday coding and general-purpose work'
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        // Explicit prompt-cache breakpoints require GPT-5.6 or newer on the OpenAI
        // Responses API. This older model keeps the capability false and falls back
        // to automatic caching via prompt_cache_key.
        supportsExplicitPromptCacheBoundary: false
      }
    }
  },
  'gpt-5.1-codex-mini': {
    id: 'gpt-5.1-codex-mini',
    label: 'GPT-5.1-Codex-Mini',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 128_000,
    // Capped at floor(contextWindowTokens * 0.9) to preserve headroom for the
    // in-flight turn on this smaller-window model. Threshold must never exceed
    // the window (enforced by an invariant test in modelCatalog.test.js).
    compactThresholdTokens: 115_200,
    attributes: { speed: 0.9, reasoning: 0.2, cost: 0.15 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: true,
      pickerLabel: 'Codex Mini 5.1',
      advancedPickerLabel: 'Codex 5.1 Mini',
      pickerDescription: 'Faster, cheaper and more efficient for routine tasks'
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['medium', 'high']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['medium', 'high']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        // Explicit prompt-cache breakpoints require GPT-5.6 or newer on the OpenAI
        // Responses API. This older model keeps the capability false and falls back
        // to automatic caching via prompt_cache_key.
        supportsExplicitPromptCacheBoundary: false
      }
    }
  },
  'gpt-5.4-mini': {
    id: 'gpt-5.4-mini',
    label: 'GPT-5.4-Mini',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 400_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.85, reasoning: 0.65, cost: 0.2 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: true,
      pickerLabel: 'Codex Mini 5.4',
      advancedPickerLabel: 'Codex 5.4 Mini',
      pickerDescription: 'Fast and efficient for everyday coding tasks'
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        // Explicit prompt-cache breakpoints require GPT-5.6 or newer on the OpenAI
        // Responses API. This older model keeps the capability false and falls back
        // to automatic caching via prompt_cache_key.
        supportsExplicitPromptCacheBoundary: false
      }
    }
  },
  'gpt-5.4-nano': {
    id: 'gpt-5.4-nano',
    label: 'GPT-5.4-Nano',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 400_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 1.0, reasoning: 0.4, cost: 0.0 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: true,
      pickerLabel: 'Codex Nano 5.4',
      advancedPickerLabel: 'Codex 5.4 Nano',
      pickerDescription: 'Fastest and cheapest for simple tasks and sub-agents'
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        // Explicit prompt-cache breakpoints require GPT-5.6 or newer on the OpenAI
        // Responses API. This older model keeps the capability false and falls back
        // to automatic caching via prompt_cache_key.
        supportsExplicitPromptCacheBoundary: false
      }
    }
  },
  'gpt-5.5': {
    id: 'gpt-5.5',
    label: 'GPT-5.5',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 1_050_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.25, reasoning: 0.95, cost: 0.9 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'GPT 5.5',
      pickerDescription: "OpenAI's flagship model"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        // Explicit prompt-cache breakpoints require GPT-5.6 or newer on the OpenAI
        // Responses API. This older model keeps the capability false and falls back
        // to automatic caching via prompt_cache_key.
        supportsExplicitPromptCacheBoundary: false
      }
    }
  },
  'gpt-5.5-pro': {
    id: 'gpt-5.5-pro',
    label: 'GPT-5.5 Pro',
    provider: 'openai',
    modelFamily: 'openai.codex',
    contextWindowTokens: 1_050_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.15, reasoning: 1.0, cost: 0.95 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'GPT 5.5 Pro',
      advancedPickerLabel: 'GPT 5.5 Pro',
      pickerDescription: 'Highest-reasoning OpenAI tier for the hardest tasks'
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'openai.responses.function_tools',
        parallelToolCalls: {
          supported: true,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'openai.response_format.json_schema'
      },
      compaction: {
        supportMode: 'native.openai'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high', 'xhigh']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 50 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'text/tab-separated-values', 'text/html', 'text/css', 'text/javascript', 'text/x-python', 'text/x-c', 'text/x-c++', 'text/x-java-source', 'text/x-typescript', 'text/x-shellscript', 'text/xml', 'text/rtf', 'application/json', 'application/javascript', 'application/xml', 'application/rtf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint', 'application/vnd.oasis.opendocument.text', 'application/x-yaml', 'application/yaml']
      },
      promptCache: {
        openAiCacheKey: {
          enabled: true
        },
        // Explicit prompt-cache breakpoints require GPT-5.6 or newer on the OpenAI
        // Responses API. This older model keeps the capability false and falls back
        // to automatic caching via prompt_cache_key.
        supportsExplicitPromptCacheBoundary: false
      }
    }
  },
  'claude-sonnet-4-6': {
    id: 'claude-sonnet-4-6',
    label: 'Claude Sonnet 4.6',
    provider: 'anthropic',
    modelFamily: 'anthropic.claude',
    contextWindowTokens: 1_000_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.6, reasoning: 0.8, cost: 0.6 },
    ui: {
      showInBasicComposer: false,
      // Superseded by claude-sonnet-5-5. Hidden from the picker but kept in the catalog
      // because existing threads, schedules, and triggers are still pinned to it.
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Sonnet 4.6',
      pickerDescription: "Anthropic's fast, capable model for everyday work"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'anthropic.messages.tools',
        parallelToolCalls: {
          supported: false,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'anthropic.messages.output_schema'
      },
      compaction: {
        supportMode: 'adapter.anthropic'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 32 * 1024 * 1024,
        maxImageSizeBytes: 5 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain']
      }
    }
  },
  'claude-sonnet-5-5': {
    id: 'claude-sonnet-5-5',
    label: 'Claude Sonnet 5.5',
    provider: 'anthropic',
    modelFamily: 'anthropic.claude',
    contextWindowTokens: 1_000_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.6, reasoning: 0.9, cost: 0.55 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: true,
      isSimpleModel: false,
      pickerLabel: 'Sonnet 5.5',
      pickerDescription: "Anthropic's newest fast, capable model for everyday work"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'anthropic.messages.tools',
        parallelToolCalls: {
          supported: false,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'anthropic.messages.output_schema'
      },
      compaction: {
        supportMode: 'adapter.anthropic'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high'],
        // Master switch for native Anthropic adaptive thinking on the thread-run path. When
        // true, eligible runs send `thinking:{type:'adaptive', display:'summarized'}` +
        // `output_config:{effort}` so the model's summarized thinking streams into the UI's
        // collapsed Reasoning block (display defaults to 'omitted' on Claude 5 — without this
        // the model still thinks, but the trace is invisible). Headless automation runs are
        // excluded in resolvePieveNativeThinkingRequest. Never emit the legacy
        // `thinking:{type:'enabled',budget_tokens}` shape — Claude 5 400s on it.
        nativeAnthropicPayload: true
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 32 * 1024 * 1024,
        maxImageSizeBytes: 5 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain']
      }
    }
  },
  'claude-opus-5-5': {
    id: 'claude-opus-5-5',
    label: 'Claude Opus 5.5',
    provider: 'anthropic',
    modelFamily: 'anthropic.claude',
    contextWindowTokens: 1_000_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.3, reasoning: 1.0, cost: 0.9 },
    ui: {
      showInBasicComposer: false,
      // Hidden from every picker: Herald's story threads pin it (projects/herald), nothing else offers it yet.
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Opus 5.5',
      pickerDescription: "Anthropic's most capable model for professional work"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'anthropic.messages.tools',
        parallelToolCalls: {
          supported: false,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'anthropic.messages.output_schema'
      },
      compaction: {
        supportMode: 'adapter.anthropic'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high'],
        // Same native adaptive-thinking contract as claude-sonnet-5-5.
        nativeAnthropicPayload: true
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 32 * 1024 * 1024,
        maxImageSizeBytes: 5 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain']
      }
    }
  },
  'claude-sonnet-5': {
    id: 'claude-sonnet-5',
    label: 'Claude Sonnet 5',
    provider: 'anthropic',
    modelFamily: 'anthropic.claude',
    contextWindowTokens: 1_000_000,
    compactThresholdTokens: 200_000,
    attributes: { speed: 0.6, reasoning: 0.85, cost: 0.55 },
    ui: {
      showInBasicComposer: false,
      // Superseded by claude-sonnet-5-5. Hidden from the picker but kept in the catalog
      // because existing threads, schedules, and triggers are still pinned to it.
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Sonnet 5',
      pickerDescription: "Anthropic's previous-generation fast, capable model"
    },
    support: {
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high']
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'anthropic.messages.tools',
        parallelToolCalls: {
          supported: false,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'anthropic.messages.output_schema'
      },
      compaction: {
        supportMode: 'adapter.anthropic'
      },
      reasoning: {
        enabled: true,
        levels: ['low', 'medium', 'high'],
        // Master switch for native Anthropic adaptive thinking on the thread-run path. When
        // true, eligible runs send `thinking:{type:'adaptive', display:'summarized'}` +
        // `output_config:{effort}` so the model's summarized thinking streams into the UI's
        // collapsed Reasoning block (display defaults to 'omitted' on Claude 5 — without this
        // the model still thinks, but the trace is invisible). Headless automation runs are
        // excluded in resolvePieveNativeThinkingRequest. Never emit the legacy
        // `thinking:{type:'enabled',budget_tokens}` shape — Claude 5 400s on it.
        nativeAnthropicPayload: true
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 32 * 1024 * 1024,
        maxImageSizeBytes: 5 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain']
      }
    }
  },
  'claude-opus-4-6': {
    id: 'claude-opus-4-6',
    label: 'Claude Opus 4.6',
    provider: 'anthropic',
    modelFamily: 'anthropic.claude',
    contextWindowTokens: 200_000,
    // Capped at floor(contextWindowTokens * 0.9) to preserve headroom for the
    // in-flight turn on this equal-window model. Threshold must never exceed
    // the window (enforced by an invariant test in modelCatalog.test.js).
    compactThresholdTokens: 180_000,
    attributes: { speed: 0.2, reasoning: 1.0, cost: 0.9 },
    ui: {
      showInBasicComposer: false,
      // Hidden from the composer: no reasoning surface (catalog reasoning off, not adaptive-
      // capable, thought_mode overlay retired), so we only expose the Sonnet line for now.
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Opus 4.6',
      pickerDescription: "Anthropic's most advanced model for professional work"
    },
    support: {
      reasoning: {
        enabled: false
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'anthropic.messages.tools',
        parallelToolCalls: {
          supported: false,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: false,
        surface: null
      },
      compaction: {
        supportMode: 'adapter.anthropic'
      },
      reasoning: {
        enabled: false,
        levels: []
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 32 * 1024 * 1024,
        maxImageSizeBytes: 5 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain']
      }
    }
  },
  'claude-opus-4-7': {
    id: 'claude-opus-4-7',
    label: 'Claude Opus 4.7',
    provider: 'anthropic',
    modelFamily: 'anthropic.claude',
    contextWindowTokens: 200_000,
    // Capped at floor(contextWindowTokens * 0.9) to preserve headroom for the
    // in-flight turn on this equal-window model. Threshold must never exceed
    // the window (enforced by an invariant test in modelCatalog.test.js).
    compactThresholdTokens: 180_000,
    attributes: { speed: 0.2, reasoning: 1.0, cost: 0.9 },
    ui: {
      showInBasicComposer: false,
      // Hidden from the composer: no reasoning surface (catalog reasoning off, not adaptive-
      // capable, thought_mode overlay retired), so we only expose the Sonnet line for now.
      showInAdvancedComposer: false,
      isSimpleModel: false,
      pickerLabel: 'Opus 4.7',
      pickerDescription: "Anthropic's most advanced model for professional work"
    },
    support: {
      reasoning: {
        enabled: false
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'anthropic.messages.tools',
        parallelToolCalls: {
          supported: false,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: false,
        surface: null
      },
      compaction: {
        supportMode: 'adapter.anthropic'
      },
      reasoning: {
        enabled: false,
        levels: []
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 32 * 1024 * 1024,
        maxImageSizeBytes: 5 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        documentMimeTypes: ['application/pdf', 'text/plain']
      }
    }
  },
  'gemini-3.1-pro-preview': {
    id: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro',
    provider: 'gemini',
    modelFamily: 'google.gemini',
    contextWindowTokens: 128_000,
    // Capped at floor(contextWindowTokens * 0.9) to preserve headroom for the
    // in-flight turn on this smaller-window model. Threshold must never exceed
    // the window (enforced by an invariant test in modelCatalog.test.js).
    compactThresholdTokens: 115_200,
    attributes: { speed: 0.7, reasoning: 0.65, cost: 0.4 },
    ui: {
      showInBasicComposer: false,
      showInAdvancedComposer: true,
      isSimpleModel: false,
      pickerLabel: 'Gemini Pro 3.1',
      advancedPickerLabel: 'Gemini 3.1 Pro',
      pickerDescription: "Google's all-purpose model for complex problem solving"
    },
    support: {
      reasoning: {
        enabled: false
      }
    },
    capabilities: {
      toolCalling: {
        enabled: true,
        surface: 'google.genai.function_calling',
        parallelToolCalls: {
          supported: false,
          requiresParallelSafeTools: true,
          minFunctionTools: 2
        }
      },
      structuredOutput: {
        supported: true,
        surface: 'google.genai.response_schema'
      },
      compaction: {
        supportMode: 'universal.openai'
      },
      reasoning: {
        enabled: false,
        levels: []
      },
      fileUpload: {
        enabled: true,
        maxFileSizeBytes: 20 * 1024 * 1024,
        imageMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
        documentMimeTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/rtf', 'text/plain', 'text/html', 'text/css', 'text/csv', 'text/rtf', 'text/xml', 'text/javascript', 'application/json']
      }
    }
  }
});

// Evaluation models answer typed questions (choice / score / yes-no) with calibrated probabilities and
// never produce free-form text, so they are NOT chat models: they live outside PIEVE_MODEL_MAP, which
// every selection, run-start, resume and picker surface reads as "a model a thread can run on", and
// isPieveSelectableModelId never accepts them. The owning feature picks the id; the catalog owns the
// metadata. `provider` is who we call and who bills us, and it keys pieve.billing_pricing_reference
// with the id exactly as a chat model's does.
const PIEVE_EVALUATION_MODEL_MAP = Object.freeze({
  'typesafe-ai/jev': Object.freeze({
    id: 'typesafe-ai/jev',
    label: 'Jev',
    provider: 'vercel',
    modelFamily: 'typesafe.jev',
    contextWindowTokens: 32_000
  })
});

export { PIEVE_AUTO_FAST_MODEL, PIEVE_AUTO_FAST_MODEL_ID, PIEVE_AUTO_MODEL, PIEVE_AUTO_MODEL_ID, PIEVE_EVALUATION_MODEL_MAP, PIEVE_MODEL_MAP, PIEVE_SENTINEL_MODELS };
